import { useDispatch, useSelector } from 'react-redux';
import {
	Checkbox,
	IconButton,
	Stack,
	TableBody,
	TableCell,
	TableRow,
	Typography,
	UnstiledLink,
} from 'styles';
import { getShortDate } from 'utils/utils';

import { ReactComponent as ChevronRightIcon } from 'assett/icons/chevron_right_icon.svg';
import ButtonDeleteClaim from 'components/commons/Dialogs/deleteClaim/ButtonDeleteClaim';
import { setTriggerRefresh } from 'feature/claims/claimSlice';
import { setSelectOne } from 'feature/claims/claimsSlice';
import { DELETE_CLAIM_REDIRECT_TO } from 'feature/hooks/deleteClaimSlice';
import useDevice from 'hooks/DesktopDevice.hook';
import useDialogDeleteClaim from 'hooks/DialogDeleteClaim.hook';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from 'store/store';
import { IClaims } from 'types/Claims';
import { SubCollections } from 'types/Form';
import { useTranslation } from 'utils/translations';
import { StatusIcon } from './StatusIcon.component';

export const ClaimsBody: FC<{}> = () => {
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: 'clientLabels',
	});
	const { t: tDbLabels } = useTranslation('', {
		keyPrefix: 'dbLabels',
	});
	const { isDesktop } = useDevice();
	const navigate = useNavigate();
	const dispatch = useDispatch<AppDispatch>();
	const deleteClaimDialog = useDialogDeleteClaim();

	const { claims, claimUuids } = useSelector((store: RootState) => store.claims);
	const progressIndicator = useSelector(
		(state: RootState) => state.progressIndicator,
	);
	const { features } = useSelector((store: RootState) => store.user);
	const featuresRole = features.map(el => el.code);

	const { subcollection } = useSelector((store: RootState) => store.form);
	const isNotifications = subcollection === SubCollections.NOTIFICATION;

	const handleClick = (claimUUID: string) => {
		navigate(
			`/${isNotifications ? 'claims-notifications' : 'view-claims'}/${claimUUID}`,
		);
	};

	const handleClickCheckAndSubmit = (
		subscriptionUuid: string,
		claimUUID: string,
	) => {
		// trigger claim refresh to load the current claim data
		dispatch(setTriggerRefresh(true));
		navigate(
			`/check-and-submit/${subscriptionUuid}/new-claim?claimUUID=${claimUUID}`,
		);
	};

	const handleClickDeleteClaim = ({
		externalClaimId,
		claimUUID,
	}: {
		externalClaimId: string;
		claimUUID: string;
	}) => {
		const payload = {
			externalClaimId,
			claimUUID,
			redirectTo: DELETE_CLAIM_REDIRECT_TO.LIST,
		};

		deleteClaimDialog.open(payload);
	};

	const List = claims?.map((claim: IClaims) => {
		const isChecked = claimUuids.includes(claim.claimUUID);
		const enableDelete = claim.status.id === 1; // status is Pending Documentation

		return (
			<TableRow key={claim.claimUUID}>
				<TableCell sx={{ width: 220 }}>
					<Checkbox
						label=""
						accessibilitylabel={`${tClientLabels('claim.labels.selectClaim')}`}
						key={claim.claimUUID}
						sx={{ p: 0, ml: 0 }}
						checked={isChecked}
						onClick={() => {
							dispatch(setSelectOne(claim.claimUUID));
						}}
					/>
				</TableCell>
				<TableCell sx={{ width: 220 }}>
					<Typography variant="body">{`#${claim.externalClaimId}`}</Typography>
				</TableCell>
				<TableCell sx={{ width: 220 }}>
					<Typography variant="body">
						{claim.claimCarrierIntegrationInfo.carrierClaimId
							? `${claim.claimCarrierIntegrationInfo.carrierClaimId}`
							: `-`}
					</Typography>
				</TableCell>
				<TableCell sx={{ width: 220 }}>
					<Typography variant="body">{claim.memberFirstName}</Typography>
				</TableCell>
				<TableCell sx={{ width: 220 }}>
					<Typography variant="body">{claim.memberLastName}</Typography>
				</TableCell>
				<TableCell sx={{ width: 220 }}>
					<Typography variant="body">{getShortDate(claim.claimDate)}</Typography>
				</TableCell>
				<TableCell sx={{ width: 220 }}>
					{(claim.status.id === 1 || claim.status.id === 18) &&
					featuresRole.includes('submit-claims') ? (
						<UnstiledLink
							variant="body"
							onClick={() => {
								handleClickCheckAndSubmit(
									claim.beneficiarySubscriptionUuid,
									claim.claimUUID,
								);
							}}
							sx={{ width: !!isDesktop ? 300 : 290 }}
						>
							<Typography variant="body">
								<StatusIcon status={claim.status.id} />
								{tDbLabels(claim.status.label)}
							</Typography>
						</UnstiledLink>
					) : (
						<Typography variant="body" sx={{ width: !!isDesktop ? 300 : 290 }}>
							<StatusIcon status={claim.status.id} />
							{tDbLabels(claim.status.label)}
						</Typography>
					)}
				</TableCell>
				<TableCell>
					<Stack
						gap={'23px'}
						pl={'10px'}
						direction={'row'}
						alignItems={'center'}
						justifyContent={'flex-end'}
					>
						{enableDelete && (
							<ButtonDeleteClaim
								iconButton
								onClickCallback={() =>
									handleClickDeleteClaim({
										externalClaimId: claim.externalClaimId,
										claimUUID: claim.claimUUID,
									})
								}
							/>
						)}
						<IconButton
							onClick={() => handleClick(claim.claimUUID)}
							sx={{ cursor: 'pointer' }}
						>
							<ChevronRightIcon />
						</IconButton>
					</Stack>
				</TableCell>
			</TableRow>
		);
	});

	return List && List.length > 0 ? (
		<TableBody>{List}</TableBody>
	) : List && !progressIndicator?.isLoading ? (
		<TableBody
			sx={{
				'& td': {
					paddingLeft: '0px!important',
					textAlign: 'left!important',
					backgroundColor: 'transparent!important',
				},
			}}
		>
			<TableRow>
				<TableCell colSpan={12}>
					<Typography variant="bodyLink" sx={{ width: '100%' }}>
						{tClientLabels('view-claims.noClaimsFound')}
					</Typography>
				</TableCell>
			</TableRow>
		</TableBody>
	) : null;
};
